import { get, has, isArray, isEmpty } from "lodash-es";

import {
  JACKPOT_ID,
  JP_CATCH_ERROR,
  JP_GET_CURRENCY,
  JP_GET_STREAM_ID_FAILURE,
  JP_GET_STREAM_ID_SUCCESS,
  JP_MARK_WIN_AS_VIEWED,
  JP_SET_SLOTS,
  JP_SET_WINS,
  JP_UPDATE_COOLDOWNS,
  JP_UPDATE_SLOTS,
  JP_UPDATE_WINS,
} from "../constants";

import { createReducer } from "./utils";

const hasSlots = (slots) => {
  return !isEmpty(slots) && slots.every((slot) => has(slot, "value"));
};

/**
 * @param {object} win
 * @return {boolean}
 */
const winsFilter = (win) => {
  const isPending = win.state === "pending";
  const isPaid = win.isCommunity && win.state === "paid" && !win.isViewed;
  const isCancel = win.state === "canceled" && !win.isViewed;

  return isPending || isPaid || isCancel;
};

/**
 * @param {Array<object>} wins
 */
const getCurrentWin = (wins) => {
  if (isEmpty(wins)) {
    return null;
  }

  return get(wins.filter(winsFilter), "[0]", null);
};

const initialState = {
  streamId: null,

  id: Date.now(),
  currency: "",

  wins: null,
  slots: null,
  cooldowns: null,
  currentWin: null,

  hasSlots: false,
};

export default createReducer(initialState, {
  [JP_GET_STREAM_ID_SUCCESS](state, { payload: streamId }) {
    return {
      ...state,
      streamId,
    };
  },

  [JP_GET_STREAM_ID_FAILURE](state) {
    return {
      ...state,
      ...initialState,
    };
  },

  [JP_GET_CURRENCY](state, { payload: currency }) {
    return {
      ...state,
      currency,
    };
  },

  [JP_SET_SLOTS](state, { payload: slots }) {
    return {
      ...state,
      slots: slots.sort((a, b) => a.slot - b.slot),
      hasSlots: hasSlots(slots),
    };
  },

  [JP_UPDATE_SLOTS](state, { payload: updatedSlots }) {
    const slots = isArray(state.slots)
      ? state.slots.map((currentSlot) => {
          const updatedSlot = (isArray(updatedSlots) ? updatedSlots : []).find(
            (slot) =>
              currentSlot.jackpotId === slot.jackpotId &&
              currentSlot.slot === slot.slot
          );
          return updatedSlot
            ? { ...currentSlot, value: updatedSlot.value }
            : currentSlot;
        })
      : [];

    return {
      ...state,
      slots,
      hasSlots: hasSlots(slots),
    };
  },

  [JP_SET_WINS](state, { payload }) {
    const wins = (isArray(payload) ? payload : []).map((win) => ({
      ...win,
      isViewed: true,
      isCommunity: get(
        state.slots.filter(
          (slot) => slot.jackpotId === win.jackpotId && slot.slot === win.slot
        ),
        "[0].isCommunity",
        false
      ),
    }));

    return {
      ...state,
      wins,
      currentWin: getCurrentWin(wins),
    };
  },

  [JP_UPDATE_WINS](state, { payload: updatedWin }) {
    const isCorrectWins = isArray(state.wins);
    const index = isCorrectWins
      ? state.wins.findIndex((win) => win.id === updatedWin.id)
      : -1;
    const wins =
      index === -1
        ? [
            ...state.wins,
            {
              ...updatedWin,
              isViewed: false,
              isCommunity: get(
                state.slots.filter(
                  (slot) =>
                    slot.jackpotId === updatedWin.jackpotId &&
                    slot.slot === updatedWin.slot
                ),
                "[0].isCommunity",
                false
              ),
            },
          ]
        : isCorrectWins
        ? state.wins.map((win, i) => {
            if (i === index) {
              return {
                ...win,
                state: updatedWin.state,
                isViewed:
                  win.state === "pending" ? updatedWin.state === "paid" : false,
              };
            }
            return win;
          })
        : [];

    return {
      ...state,
      currentWin: getCurrentWin(wins),
      wins,
    };
  },

  [JP_UPDATE_COOLDOWNS](state, { payload }) {
    if (!isArray(payload)) {
      return null;
    }

    const cooldowns = { ...state.cooldowns };

    payload
      .filter((cooldown) => cooldown["jackpot_id"] === JACKPOT_ID)
      .forEach((c) => {
        cooldowns[c.slot] = Date.now() + c.cooldown_ms;
      });

    return {
      ...state,
      cooldowns: !isEmpty(cooldowns) ? cooldowns : null,
    };
  },

  [JP_MARK_WIN_AS_VIEWED](state, { payload: { winId } }) {
    const wins = state.wins.map((win) =>
      win.id !== winId ? win : { ...win, isViewed: true }
    );

    return {
      ...state,
      wins,
      currentWin: getCurrentWin(wins),
    };
  },

  [JP_CATCH_ERROR](state) {
    return {
      ...state,
      ...initialState,
    };
  },
});
